/* eslint-disable react/no-unused-prop-types */
import { isEqual } from "lodash";
import React, { ReactElement, useLayoutEffect } from "react";
import { RectLayout } from "../../../GeometricLayout";
import usePrevious from "./usePrevious";
import { TreeRange } from "../../../writeshift/Range";
import { AttachedEditor } from "../../../editorsSlice";
import { WritingAlert } from "../writingAlertsSlice";
import treeRangeToOverlayPosition from "../treeRangeToOverlayPosition";

type BarebonesAlert = { id: number; range: TreeRange };
type CalculationResult = { rects: RectLayout[]; alert: BarebonesAlert }[];

interface Props {
  editorNode: Node;
  alerts: WritingAlert[];
  editor: AttachedEditor;
  onCalculation: (result: CalculationResult) => void;
  deps: unknown[];
}

/**
 * Calculates the rectangles in which underlines and highlights should be drawn.
 *
 * Updates when the editor or alerts change. Only updates stale or not-drawn underlines when
 * necessary.
 *
 * @param alerts the alerts, which contain the positions for the underlines
 * @param editor the editor in charge of the textNode and alerts
 * @param onCalculation a callback which updates with calculations for drawing underlines
 */
export default function useUnderlineCalculations({
  editorNode,
  alerts,
  editor,
  onCalculation,
  deps,
}: Props): void {
  const { scrollLeft, scrollTop } = editor;
  const { top, left, width, height } = editor.boundingClientRect;
  const { top: borderTop, left: borderLeft } = editor.border;
  const lastBoundingClientRect = usePrevious({ top, left, width, height });

  // TODO(perf): we could optimize this hook a lot more, potentially only updating things that have changed or are visible, etc.
  // This is using layout effect because it needs to measure the range
  // for each highlight range (get the RectLayout). It must be run
  // synchronously after React renders the text in the editor in order
  // to get an accurate RectLayout.
  // See https://kentcdodds.com/blog/useeffect-vs-uselayouteffect
  useLayoutEffect(() => {
    const shouldUpdateAll = !isEqual(
      lastBoundingClientRect,
      editor.boundingClientRect
    );
    const bareBonesAlerts = alerts
      .filter((a) => {
        if (shouldUpdateAll) {
          return true;
        }
        return a.ui.overlayPositions.length === 0 || a.ui.staleRect;
      })
      .map((a) => {
        return {
          id: a.id,
          range: a.ui.range,
        };
      });
    const result: CalculationResult = [];
    for (const alert of bareBonesAlerts) {
      const rects = treeRangeToOverlayPosition(
        alert.range,
        editorNode,
        { scrollLeft, scrollTop },
        {
          top: top + borderTop,
          left: left + borderLeft,
        }
      );
      result.push({ rects: rects || [], alert });
    }
    result.length && onCalculation(result);
  }, [
    editorNode,
    alerts,
    borderLeft,
    borderTop,
    top,
    left,
    scrollLeft,
    scrollTop,
    onCalculation,
    lastBoundingClientRect,
    editor.boundingClientRect,

    // eslint-disable-next-line react-hooks/exhaustive-deps
    ...deps,
  ]);
}

export function UnderlineCalculations(props: Props): ReactElement {
  useUnderlineCalculations(props);
  return <></>;
}
